import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { useEffect } from "react";

import { SiteProvider } from "@/lib/site";
import { AuthProvider } from "@/lib/auth";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFloating from "@/components/WhatsAppFloating";
import ChatBot from "@/components/ChatBot";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import { Industries, CaseStudies, CaseStudyDetail, Blog, BlogPost,
  Testimonials, FaqPage, CertificationsPage, StaticPageView, NotFound,
} from "@/pages/Misc";
import IndustryDetail from "@/pages/IndustryDetail";

import AdminLayout from "@/admin/AdminLayout";
import AdminLogin from "@/admin/AdminLogin";
import AuthCallback from "@/admin/AuthCallback";
import AdminDashboard from "@/admin/Dashboard";
import {
  AdminProducts, AdminCategories, AdminIndustries, AdminTestimonials,
  AdminFaqs, AdminCertifications, AdminBlog, AdminCaseStudies,
  AdminPages, AdminEnquiries, AdminSettings,
} from "@/admin/AdminPages";
import { AdminAboutPage, AdminHomePage } from "@/admin/AdminContentPages";

import { Toaster } from "@/components/ui/sonner";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function PublicLayout() {
  return (
    <>
      <SiteHeader />
      <main><Outlet /></main>
      <SiteFooter />
      <WhatsAppFloating />
      <ChatBot />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Process OAuth callback synchronously before any other route
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public site */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/category/:slug" element={<Products />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<IndustryDetail />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/certifications" element={<CertificationsPage />} />
          <Route path="/privacy-policy" element={<StaticPageView />} />
          <Route path="/terms" element={<StaticPageView />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="industries" element={<AdminIndustries />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="case-studies" element={<AdminCaseStudies />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="faqs" element={<AdminFaqs />} />
          <Route path="certifications" element={<AdminCertifications />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="page-home" element={<AdminHomePage />} />
          <Route path="page-about" element={<AdminAboutPage />} />
          <Route path="enquiries" element={<AdminEnquiries />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SiteProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster />
          </AuthProvider>
        </SiteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
